import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import React, { Dispatch, ReactElement, SetStateAction } from 'react';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { GetSelectedKeywordsStatsGq } from '../../../../graphql/projects/queries/useGetSelectedKeywordsStatsGq';
import DataTableToolbarButton from '../../../voxel/DataTableToolbar/DataTableToolbarButton';
import DataTableToolbarContainer from '../../../voxel/DataTableToolbar/DataTableToolbarContainer';
import DataTableToolbarSeparator from '../../../voxel/DataTableToolbar/DataTableToolbarSeparator';
import { KeywordTablePage } from './KeywordTable';
import KeywordTableColumnPicker from './KeywordTableColumnPicker/KeywordTableColumnPicker';
import AddKeywordsButton from './ToolbarButtons/AddKeywordsButton';
import AddRankDataButton from './ToolbarButtons/AddRankDataButton';
import ArchiveButton from './ToolbarButtons/ArchiveButton';
import CopyButton from './ToolbarButtons/CopyButton';
import DomainsButton from './ToolbarButtons/DomainsButton';
import DownloadButton from './ToolbarButtons/DownloadButton';
import EditKeywordsButton from './ToolbarButtons/EditKeywordsButton';
import RankColorButton from './ToolbarButtons/RankColorButton';
import TagsButton from './ToolbarButtons/TagsButton';
import TranslateButton from './ToolbarButtons/TranslateButton';
import UnarchiveButton from './ToolbarButtons/UnarchiveButton';
import VolumeTrendButton from './ToolbarButtons/VolumeTrendButton';
import { KeywordTableState } from './logic/useKeywordTableState';
import { CurrentUserGq } from '../../../../graphql/users/models/CurrentUserGq';

export interface KeywordTableToolbarProps {
  project?: ProjectGq;
  tableState: KeywordTableState;
  reloadKeywords: () => Promise<any>;
  onDownload?: () => void;
  selectedKeywordStats?: GetSelectedKeywordsStatsGq;
  isScrolling?: boolean;
  setDrawerOpen?: Dispatch<SetStateAction<boolean>>;
  page?: KeywordTablePage;
  extraButtons?: ReactElement;
  setOpenRanksDialog?: Dispatch<SetStateAction<boolean>>;
  setOpenAddDomainDialog?: Dispatch<SetStateAction<DomainTypeGq>>;
  changeVisibleColumns?: boolean;
  openGetRanksDialog?: boolean;
  user: CurrentUserGq;
}

export const KeywordTableToolbar: React.FC<KeywordTableToolbarProps> = React.memo(
  ({
    extraButtons,
    isScrolling,
    tableState,
    project,
    setOpenAddDomainDialog,
    setOpenRanksDialog,
    onDownload,
    reloadKeywords,
    selectedKeywordStats,
    changeVisibleColumns,
    page,
    openGetRanksDialog,
    user,
  }) => {
    let selectionActive = false;
    if (!!tableState.backendSelectionState?.keywordIds.length) selectionActive = true;
    if (!!tableState.backendSelectionState?.invertSelection) selectionActive = true;
    const selectedCount = selectionActive ? selectedKeywordStats.selectedCount : 0;

    const drawerOpen =
      tableState.tableUIState.showDomainsPanel || tableState.tableUIState.showTagPanel;

    const toolbarSections: React.ReactNode[] = [];

    // Quick search page
    if (!!extraButtons) {
      toolbarSections.push(<div className="ml-1">{extraButtons}</div>);
    }

    // Add data buttons
    if (page === 'dm') {
      toolbarSections.push(
        <>
          <AddKeywordsButton project={project} reload={reloadKeywords} user={user} />
          <AddRankDataButton
            project={project}
            setOpenRanksDialog={setOpenRanksDialog}
            setOpenAddDomainDialog={setOpenAddDomainDialog}
            openGetRanksDialog={openGetRanksDialog}
            selectionActive={selectionActive}
          />
        </>
      );
    }

    // Domains/tags
    if (page === 'dm') {
      toolbarSections.push(
        <>
          <DomainsButton tableState={tableState} project={project} />
          <TagsButton tableState={tableState} project={project} selectionActive={selectionActive} />
        </>
      );
    }

    // Download buttons
    toolbarSections.push(
      <>
        <CopyButton
          tableState={tableState}
          selectionActive={selectionActive}
          selectedCount={selectedCount}
        />
        <DownloadButton
          onDownload={onDownload}
          project={project}
          tableState={tableState}
          selectionActive={selectionActive}
          selectedCount={selectedCount}
        />
      </>
    );

    // Archive buttons
    if (page !== 'progress') {
      toolbarSections.push(
        <>
          <ArchiveButton
            reloadKeywords={reloadKeywords}
            tableState={tableState}
            selectedCount={selectedCount}
            selectionActive={selectionActive}
          />
          <UnarchiveButton
            reloadKeywords={reloadKeywords}
            tableState={tableState}
            selectedCount={selectedCount}
            selectionActive={selectionActive}
          />
        </>
      );
    }

    // Modification buttons
    if (page === 'dm') {
      toolbarSections.push(
        <>
          <EditKeywordsButton
            selectedKeywordStats={selectedKeywordStats}
            selectedCount={selectedCount}
            tableState={tableState}
            selectionActive={selectionActive}
          />
          <TranslateButton tableState={tableState} selectedKeywordStats={selectedKeywordStats} />
        </>
      );
    }

    // Column management buttons
    if (page === 'dm' || page === 'progress' || changeVisibleColumns) {
      toolbarSections.push(
        <>
          <RankColorButton tableState={tableState} />
          <VolumeTrendButton keywordTableState={tableState} />
          <KeywordTableColumnPicker
            project={project}
            keywordColumnPickerBag={tableState.columnPickerBag}
            isProgressPage={page === 'progress'}
          />
        </>
      );
    }

    // Selection buttons
    if (tableState.hasFiltering || selectedCount) {
      toolbarSections.push(
        <>
          {tableState.hasFiltering && (
            <>
              <DataTableToolbarButton
                label="Active filters"
                tooltip="Clear all active table filters"
                iconProp={faTimes}
                toggle
                effect="primary"
                effectAnimate={false}
                onClick={() => tableState.resetFilterState()}
                disabled={!tableState.hasFiltering}
              />
              <div className="w-2" />
            </>
          )}
          {!!selectedCount && (
            <DataTableToolbarButton
              toggle
              effect="primary"
              effectAnimate={false}
              label={` ${selectedCount ? selectedCount : '0'} selected`}
              tooltip={selectedCount > 0 && 'Clear selected keywords'}
              iconProp={faTimes}
              onClick={() => tableState.handleResetSelection()}
            />
          )}
        </>
      );
    }

    return (
      <DataTableToolbarContainer
        className={`
            ${isScrolling && page === 'dm' ? 'sticky z-50 transition duration-150' : ''}
            ${isScrolling && page === 'dm' && drawerOpen ? 'top-[412px]' : ''}
            ${isScrolling && page === 'dm' ? 'top-12' : ''}
            ${isScrolling && page !== 'dm' && !drawerOpen ? 'top-[55px]' : ''}
        `}
      >
        {toolbarSections.map((section, index) => (
          <React.Fragment key={index}>
            {index !== 0 && <DataTableToolbarSeparator />}
            {section}
          </React.Fragment>
        ))}
      </DataTableToolbarContainer>
    );
  }
);

export default KeywordTableToolbar;
